import { FC, useEffect, useRef } from 'react';
import { openPdf } from './pdfDocument';
import { PageCanvas } from './styles';

const SIZE_TOLERANCE = 0.5;
const MAX_SCALE = 3;
const RENDER_TIMEOUT_MS = 15000;

export type PageImageProblem = 'size_mismatch' | 'render_failed';

interface PdfPageImageProps {
  url: string;
  page: number;
  width: number;
  height: number;
  onProblem: (problem: PageImageProblem, detail: Record<string, number | string>) => void;
}

const PdfPageImage: FC<PdfPageImageProps> = ({ url, page, width, height, onProblem }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const onProblemRef = useRef(onProblem);
  onProblemRef.current = onProblem;

  useEffect(() => {
    let live = true;
    let cancel = () => undefined as void;
    let timeout: number | undefined;

    openPdf(url)
      .then((pdf) => pdf.getPage(page))
      .then((pdfPage) => {
        if (!live) return undefined;
        const natural = pdfPage.getViewport({ scale: 1 });
        if (
          Math.abs(natural.width - width) > SIZE_TOLERANCE ||
          Math.abs(natural.height - height) > SIZE_TOLERANCE
        ) {
          onProblemRef.current('size_mismatch', {
            page,
            rendered_width: natural.width,
            rendered_height: natural.height,
            evidence_width: width,
            evidence_height: height
          });
          return undefined;
        }
        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d');
        if (!canvas || !context) return undefined;
        const scale = Math.min(Math.max(window.devicePixelRatio || 1, 1), MAX_SCALE);
        const viewport = pdfPage.getViewport({ scale });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        timeout = window.setTimeout(() => {
          if (live)
            onProblemRef.current('render_failed', { page, error_type: 'timeout', status: 'none' });
        }, RENDER_TIMEOUT_MS);
        const task = pdfPage.render({ canvasContext: context, viewport });
        cancel = () => task.cancel();
        return task.promise.then(() => {
          window.clearTimeout(timeout);
          pdfPage.cleanup();
        });
      })
      .catch((error: Error & { status?: number }) => {
        window.clearTimeout(timeout);
        if (live && error?.name !== 'RenderingCancelledException') {
          onProblemRef.current('render_failed', {
            page,
            error_type: error?.name ?? 'unknown',
            status: error?.status ?? 'none'
          });
        }
      });

    return () => {
      live = false;
      window.clearTimeout(timeout);
      cancel();
    };
  }, [url, page, width, height]);

  return <PageCanvas ref={canvasRef} aria-hidden="true" />;
};

export default PdfPageImage;
